import { MemberExperienceUserFromIdToken } from '@guildeducationinc/auth-frontend-tools';
import { MultiIndexQueryParams } from 'src/clients/Algolia';

export interface FilterParams {
  facetFilters: MultiIndexQueryParams['params']['facetFilters'];
  filters: MultiIndexQueryParams['params']['filters'];
}

export interface GoalCategory {
  name: string;
  facets: string[];
  nonDegree?: boolean;
  disabled?: boolean;
}

export interface SubInterestCategory {
  name: string;
  description: string;
  facet: string;
}

export interface QuizAnalytics {
  questionKey: string;
  responseKey?: string | string[];
  questionType: string;
}

export interface Answers {
  [AnswerKeys.factor]?: string;
  [AnswerKeys.credits]?: string;
  [AnswerKeys.education]?: string;
  [AnswerKeys.feeling]?: string;
  [AnswerKeys.goal]?: string;
  [AnswerKeys.hsGpa]?: string;
  [AnswerKeys.collegeGpa]?: string;
  [AnswerKeys.interests]?: string[];
  [AnswerKeys.techInterests]?: string[];
  [AnswerKeys.businessInterests]?: string[];
  [AnswerKeys.healthcareInterests]?: string[];
  [AnswerKeys.weeklyTime]?: string;
  [AnswerKeys.timeToComplete]?: string;
}

export interface ScreenProps {
  createRecs?: () => void;
  defaultAnswers: Partial<Answers>;
  nextScreen: () => void;
  previousScreen: () => void;
  updateAnswers: (answer: Partial<Answers>, answers: Answers) => void;
  user: MemberExperienceUserFromIdToken;
}

export enum Screens {
  OPTIN = 'OPTIN',
  WELCOME_1 = 'WELCOME_1',
  WELCOME_2 = 'WELCOME_2',
  FEELING_1 = 'FEELING_1',
  FEELING_2 = 'FEELING_2',
  EDUCATION = 'EDUCATION',
  GOAL = 'GOAL',
  TIME_TO_COMPLETE = 'TIME_TO_COMPLETE',
  WEEKLY_TIME = 'WEEKLY_TIME',
  HS_GPA = 'HS_GPA',
  COLLEGE_GPA = 'COLLEGE_GPA',
  CREDITS = 'CREDITS',
  INTERESTS = 'INTERESTS',
  TECH_INTERESTS = 'TECH_INTERESTS',
  BUSINESS_INTERESTS = 'BUSINESS_INTERESTS',
  HEALTHCARE_INTERESTS = 'HEALTHCARE_INTERESTS',
  FACTOR = 'FACTOR',
  LOADING = 'LOADING',
}

export enum ScreenGroups {
  WELCOME = 'WELCOME',
  FEELING = 'FEELING',
  EDUCATION = 'EDUCATION',
  INTERESTS = 'INTERESTS',
  TIME_TO_COMPLETE = 'TIME_TO_COMPLETE',
  WEEKLY_TIME = 'WEEKLY_TIME',
  FACTOR = 'FACTOR',
  LOADING = 'LOADING',
}

export enum AnswerKeys {
  factor = 'factor',
  credits = 'credits',
  education = 'education',
  feeling = 'feeling',
  hsGpa = 'hsGpa',
  goal = 'goal',
  collegeGpa = 'collegeGpa',
  interests = 'interests',
  techInterests = 'techInterests',
  businessInterests = 'businessInterests',
  healthcareInterests = 'healthcareInterests',
  timeToComplete = 'timeToComplete',
  weeklyTime = 'weeklyTime',
}

export enum AnswerScreens {
  collegeGpa = 'COLLEGE_GPA',
  credits = 'CREDITS',
  education = 'EDUCATION',
  feeling = 'FEELING_1',
  goal = 'GOAL',
  hsGpa = 'HS_GPA',
  interests = 'INTERESTS',
  timeToComplete = 'TIME_TO_COMPLETE',
  weeklyTime = 'WEEKLY_TIME',
}

export enum AnswerScreensToCatalogServiceScreenName {
  collegeGpa = 'COLLEGE_GPA',
  credits = 'CREDITS',
  education = 'EDUCATION',
  feeling = 'FEELING',
  goal = 'GOAL',
  hsGpa = 'HS_GPA',
  interests = 'INTERESTS',
  timeToComplete = 'TIME_TO_COMPLETE',
  weeklyTime = 'WEEKLY_TIME',
}

export enum EducationValues {
  'Some high school' = 'Some high school',
  'High school diploma' = 'High school diploma',
  'Some college credits' = 'Some college credits',
  'Associate degree' = 'Associate degree',
  "Bachelor's degree" = "Bachelor's degree",
  "Master's degree or higher" = "Master's degree or higher",
}

export enum GoalValues {
  college = 'Earn a college degree',
  masters = "Earn a master's degree",
  certificate = 'Earn a certificate or career diploma',
  highschool = 'Finish high school',
  language = 'Learn or improve a language',
}

export enum GpaRanges {
  threePointFive = '3.5-4.0 (A- to A+)',
  threePointZero = '3.0-3.4 (B to B+)',
  twoPointFive = '2.5-2.9 (C+ to B-)',
  twoPointZero = '2.0-2.4 (C to C+)',
  onePointZero = '0-1.9 (F to C-)',
  notSure = "I'm not sure",
}

export enum CreditsValues {
  'Less than 12 credits' = 'Less than 12 credits',
  '12-29 credits' = '12-29 credits',
  '30-60 credits' = '30-60 credits',
  'More than 60 credits' = 'More than 60 credits',
  "I'm not sure" = "I'm not sure",
}

export enum TimeToCompleteValues {
  upToTwoMonths = 'Up to 2 months',
  upToSixMonths = 'Up to 6 months',
  upToTwoYears = 'Up to 2 years',
  upToFourYears = 'Up to 4 years',
  moreThanFourYears = 'More than 4 years',
  notSure = "I'm not sure",
}

export enum WeeklyTimeValues {
  upToTwo = 'Up to 2 hours per week',
  upToSix = 'Up to 6 hours per week',
  upToTwelve = 'Up to 12 hours per week',
  upToTwentyFour = 'Up to 24 hours per week',
  moreThan24 = 'More than 24 hours per week',
  notSure = "I'm not sure",
}

export enum AlertMessages {
  weeklyTime = "Some options below are not available because the goals you've shared require more time per week.",
  timeToComplete = "Some options below are not available because the goals you've shared require more time to complete.",
}
